import { BellSolid, CircleCheckSolid, CircleExclamationSolid, TriangleExclamationSolid } from '@allocab/allocab-icons';
import clsx from 'clsx';
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useState } from 'react';

import { ToastConfig, ToastInterface } from './types';

const DEFAULT_TOAST_CONFIG: ToastConfig = {
  message: '',
  severity: 'info',
  timer: 5000,
};

const iconForSeverity: Record<ToastConfig['severity'], ReactNode> = {
  success: <CircleCheckSolid width={24} height={24} className="fill-success" />,
  info: <BellSolid width={24} height={24} className="fill-info" />,
  warning: <TriangleExclamationSolid width={24} height={24} className="fill-warning" />,
  error: <CircleExclamationSolid width={24} height={24} className="fill-error" />,
};

const colorForSeverity: Record<ToastConfig['severity'], string> = {
  success: 'text-success',
  info: 'text-info',
  warning: 'text-warning',
  error: 'text-error',
};

const Toast = forwardRef<ToastInterface>((_props, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>(DEFAULT_TOAST_CONFIG);

  const show: ToastInterface['show'] = config => {
    setToastConfig({
      message: config.message ?? DEFAULT_TOAST_CONFIG.message,
      severity: config.severity || DEFAULT_TOAST_CONFIG.severity,
      timer: config.timer ?? DEFAULT_TOAST_CONFIG.timer,
    });
    setIsVisible(true);
  };

  const hide: ToastInterface['hide'] = () => {
    setIsVisible(false);
  };

  const handleDismiss = () => {
    hide();
  };

  useImperativeHandle(ref, () => ({ show, hide }));

  useEffect(() => {
    if (isVisible && toastConfig?.timer != null && toastConfig.timer > 0) {
      const timer = setTimeout(hide, toastConfig.timer);

      return () => clearTimeout(timer);
    }
  }, [toastConfig?.timer, isVisible]);

  return (
    <div className={clsx('fixed flex top-4 left-0 right-0 justify-center z-[120]', !isVisible && 'hidden')}>
      <div className="shadow-lg border bg-background px-4 py-2 rounded flex flex-row items-center mx-4 sm:max-w-2xl">
        {iconForSeverity[toastConfig.severity]}
        <p className="ml-4">{toastConfig.message}</p>
        <div className="border-l h-full mx-4" />
        <button className="self-stretch" onClick={handleDismiss}>
          <p className={colorForSeverity[toastConfig.severity]}>Fermer</p>
        </button>
      </div>
    </div>
  );
});

Toast.displayName = 'Toast';

export { Toast };
